import { useRef, useEffect } from 'react';
import classnames from 'classnames';

import classes from './PieChart.module.scss';

const PieChart = props => {
	const defaultColors = [
		'red',
		'orange',
		'yellow',
		'green',
		'blue',
		'violet',
	];

	const sumPieTuples = (dataset, key) => {
		return dataset.reduce((a, b) => a + (b[key] || 0), 0);
	};

	const draw = (canvas, ctx, dataset, options) => {
		const sum = sumPieTuples(dataset, 'value');
		const marginTop = options && options.title ? 30 : 0;
		const marginBottom =
			options && options.legend
				? 15 + (dataset ? 15 * dataset.length : 0)
				: 0;
		const verticalMargin = marginTop + marginBottom;
		const canvasWidth = canvas.width;
		const canvasHeight = canvas.height - verticalMargin;
		const canvasCenterX = canvasWidth / 2;
		const canvasCenterY = canvasHeight / 2;
		const radius =
			canvasWidth < canvasHeight ? canvasWidth / 2 : canvasHeight / 2;
		// const defaultColors = generateDefaultColors(dataset.length);
		let legendInfo = [];

		//apply title
		if (options.title) {
			ctx.fillStyle = 'black';
			ctx.font = generateFont(20);
			ctx.textAlign = 'center';
			ctx.fillText(options.title, canvasCenterX, marginTop / 2);
		}

		//draw the slices
		let currentAngle = 0;
		let i = 0;
		ctx.save();
		ctx.translate(0, marginTop);
		for (const dataKey in dataset) {
			const sliceAngle = (2 * Math.PI * dataset[dataKey].value) / sum;
			const color = dataset[dataKey].color
				? dataset[dataKey].color
				: defaultColors[i % defaultColors.length];
			drawPieSlice(
				ctx,
				canvasCenterX,
				canvasCenterY,
				radius,
				currentAngle,
				currentAngle + sliceAngle,
				convertColor(color)
			);

			//apply label

			if (options && options.labels) {
				let labelX =
					canvasCenterX +
					(radius / 2) * Math.cos(currentAngle + sliceAngle / 2);
				let labelY =
					canvasCenterY +
					(radius / 2) * Math.sin(currentAngle + sliceAngle / 2);
				if (options.isDonut) {
					const offset = (radius * 0.5) / 2;
					labelX =
						canvasCenterX +
						(offset + radius / 2) *
							Math.cos(currentAngle + sliceAngle / 2);
					labelY =
						canvasCenterY +
						(offset + radius / 2) *
							Math.sin(currentAngle + sliceAngle / 2);
				}
				const text =
					dataset[dataKey].label +
					': ' +
					Math.round((dataset[dataKey].value / sum) * 100) +
					'%';
				ctx.fillStyle = 'black';
				ctx.font = generateFont(15);
				ctx.textAlign = 'center';
				ctx.textBaseline = 'middle';
				ctx.fillText(text, labelX, labelY);
			}

			const lbl =
				dataset[dataKey] && dataset[dataKey].label
					? dataset[dataKey].label
					: '???';
			legendInfo.push({
				label: lbl ? lbl : '',
				color: color ? convertColor(color) : '',
			});

			currentAngle += sliceAngle;
			i++;
		}

		//style for donut
		if (options.isDonut) {
			//draw hole in middle
			drawPieSlice(
				ctx,
				canvasCenterX,
				canvasCenterY,
				radius / 2,
				0,
				2 * Math.PI,
				convertColor('white')
			);

			//apply label
			if (options.donutLabel) {
				ctx.fillStyle = 'black';
				ctx.font = generateFont(20);
				ctx.textAlign = 'center';
				ctx.textBaseline = 'middle';
				ctx.fillText(options.donutLabel, canvasCenterX, canvasCenterY);
			}
		}
		ctx.restore();

		const yPad = 15;
		const width = 10;
		const height = 10;
		const rectMarginRight = 5;
		//apply the legend
		for (let i = 0; i < legendInfo.length; i++) {
			ctx.font = generateFont(15);
			ctx.textAlign = 'left';

			const text = legendInfo[i].label;
			const textMetrics = ctx.measureText(text);
			const textWidth =
				Math.abs(textMetrics.actualBoundingBoxLeft) +
				Math.abs(textMetrics.actualBoundingBoxRight);

			// center to canvas, accounting for rectangle, text width and space between rectangle, text
			const x = canvasCenterX - (textWidth + width + rectMarginRight) / 2;
			// incrementally distance
			const y = canvasHeight + marginTop + yPad + i * yPad;

			ctx.fillStyle = legendInfo[i].color;
			ctx.fillRect(x, y, width, height);

			ctx.fillStyle = 'black';
			ctx.fillText(text, x + width + rectMarginRight, y + height);
		}
	};
	const drawPieSlice = (
		ctx,
		centerX,
		centerY,
		radius,
		startAngle,
		endAngle,
		color
	) => {
		ctx.fillStyle = color;
		ctx.beginPath();
		ctx.moveTo(centerX, centerY);
		ctx.arc(centerX, centerY, radius, startAngle, endAngle);
		ctx.closePath();
		ctx.fill();
	};

	/**
	 * Attempts to convert @param color to value of "theme" equivalent.
	 * Supported colors for conversion defined in associated stylesheet.
	 * @param {string} color intended CSS color value.
	 * @returns conditionally converted CSS color value.
	 */
	const convertColor = color => {
		return Object.keys(classes).includes(color) ? classes[color] : color;
	};

	/**
	 * Generates font shorthand property.
	 * Includes properties 'font-weight', 'font-size', 'line-height', 'font-family'.
	 * @param {Number} fontSize size of font.
	 * @returns {String} CSS font shorthand property.
	 */
	const generateFont = fontSize => {
		return (
			'500' +
			' ' +
			fontSize +
			'px/20px' +
			' ' +
			classes['mdc-typography-font-family']
		);
	};

	useEffect(() => {
		if (canvasRef && canvasRef.current) {
			const canvas = canvasRef.current;
			if (canvas) {
				const ctx = canvas.getContext('2d');
				if (ctx) {
					ctx.clearRect(0, 0, window.innerHeight, window.innerWidth);
					const dataset = [...data];
					draw(canvas, ctx, dataset, options ? options : {});
				}
			}
		}
	});

	const canvasRef = useRef(null);
	const { width, height, data } = props;
	const { options } = props;
	const { className } = props;

	return (
		<div className={classnames(className, classes['pie-chart__container'])}>
			<canvas ref={canvasRef} width={width} height={height} />
		</div>
	);
};

export default PieChart;
